import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import "react-dropzone-uploader/dist/styles.css";
import {
  getParams,
  navigate,
  sendPatchRequest,
  sendPostRequest,
  sendGetRequest,
  toTzTime,
  allowedOperations,
} from "../utils/utils";
import EditPage from "./component/edit-page";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useState, useEffect } from "react";
import AppRoutes from "../routing/routes";
import EasyTextEdit from "./component/text-edit";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDateTimePicker } from "@mui/x-date-pickers";
import { coreStyles } from "../theme/style";

const notificationRef = {
  type: "popup",
  heading: "",
  title: "",
  imageUrl: "",
  message: "",
  action: "None",
};

const NotificationEdit = () => {
  const appStyle = coreStyles();
  const { uuid } = getParams();
  var [notification, setNotification] = useState(notificationRef);
  const [schedule, setSchedule] = useState(false);
  const { canCreate } = allowedOperations();

  const [status, setStatus] = useState({
    error: false,
    loading: false,
    message: null,
  });
  useEffect(() => {
    sendGetRequest(
      `notifications?uuid=${uuid}`,
      (loading) => setStatus({ ...{ loading: loading } }),
      (data) => {
        if (data) {
          setSchedule(data.scheduledAt !== null);
          setNotification({ ...notificationRef, ...data });
        }
      }
    );
    return () => setNotification(notificationRef);
    // eslint-disable-next-line
  }, [uuid]);

  const createUpdateNotification = () => {
    if (uuid) {
      delete notification.uuid;
      sendPatchRequest(
        `notifications/${uuid}`,
        notification,
        (loading) => setStatus({ ...{ loading: loading } }),
        (data) => {
          setStatus({
            ...{
              error: data.error,
              message: data.message,
            },
          });
        }
      );
    } else {
      sendPostRequest(
        "notifications",
        notification,
        (loading) => setStatus({ ...{ loading: loading } }),
        (data) => {
          setStatus({
            ...{
              error: data.error,
              message: data.message,
            },
          });
        }
      );
    }
  };

  const onValueChange = (value, tag) =>
    setNotification({ ...notification, [tag]: value });

  return (
    <EditPage
      title={uuid ? "Edit Notification" : "Add Notification"}
      status={status}
      btnLabel={canCreate ? (uuid ? "Update Now" : "Add Now") : null}
      onSave={() => createUpdateNotification()}
      onBack={() => navigate(`${AppRoutes.notifications}`, true)}
      validated={
        notification.heading &&
        notification.title &&
        notification.message &&
        !status.loading
      }
      onFinish={() => {
        setStatus({ ...{ loading: false, message: null, error: false } });
        if (!status.error) {
          setNotification({ ...notificationRef });
          navigate(`${AppRoutes.notifications}`, true);
        }
      }}
    >
      <Grid item md={2} xs={12}>
        <FormControl sx={{ minWidth: 100 }} fullWidth>
          <InputLabel id="type-label">Notification Type</InputLabel>
          <Select
            value={notification.type}
            label="Notification Type"
            labelId="type-label"
            id="type-select"
            autoWidth
            onChange={(e) => onValueChange(e.target.value, "type")}
          >
            {[
              { value: "popup", label: "Pop-Up Notification" },
              { value: "push", label: "Push Notification" },
            ].map((type) => {
              return (
                <MenuItem value={type.value}>
                  &nbsp;&nbsp;&nbsp;{type.label}
                  <br />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>

      <Grid item md={5} xs={12}>
        <EasyTextEdit
          label="Notification Heading"
          placeholder="Heading for the notification"
          type="text"
          id="heading"
          errorText=""
          textField={true}
          disabled={status.loading}
          value={notification.heading}
          onChange={onValueChange}
        />
      </Grid>

      <Grid item md={5} xs={12}>
        <EasyTextEdit
          label="Notification Title"
          placeholder="Title for the notification"
          type="text"
          id="title"
          errorText=""
          textField={true}
          disabled={status.loading}
          value={notification.title}
          onChange={onValueChange}
        />
      </Grid>

      <Grid item md={12} xs={12}>
        <EasyTextEdit
          placeholder="Message for the notification"
          label="Notification Message"
          type="text"
          id="message"
          textField={true}
          errorText=""
          multiline={4}
          disabled={status.loading}
          value={notification.message}
          onChange={onValueChange}
        />
      </Grid>

      <Grid item md={schedule ? 6 : 7} xs={12}>
        <EasyTextEdit
          placeholder="Notification Image URL"
          label="Notification Image URL"
          type="text"
          id="imageUrl"
          textField={true}
          errorText=""
          disabled={status.loading}
          value={notification.imageUrl}
          onChange={onValueChange}
        />
      </Grid>

      <Grid item md={schedule ? 2 : 3} xs={12}>
        <FormControl sx={{ minWidth: 100 }} fullWidth>
          <InputLabel id="action-label">Notification Action</InputLabel>
          <Select
            value={notification.action}
            label="Notification Action"
            labelId="action-label"
            id="action-select"
            autoWidth
            onChange={(e) => {
              if (notification.action !== e.target.value) {
                onValueChange(e.target.value, "action");
              }
            }}
          >
            {[
              { value: "None", label: "No Action" },
              { value: "ACTION_UPDATE", label: "Update App" },
              { value: "ACTION_CONTINUE", label: "Continue" },
            ].map((type) => {
              return (
                <MenuItem value={type.value}>
                  &nbsp;&nbsp;&nbsp;{type.label}
                  <br />
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Grid>

      <Grid item md={2} xs={12}>
        <FormControlLabel
          sx={{ mt: 2, mr: 4 }}
          checked={schedule}
          control={<Switch />}
          disabled={status.loading}
          onChange={(event) => {
            setSchedule(event.target.checked);
            if (!event.target.checked) {
              setNotification({ ...notification, scheduledAt: null });
            }
          }}
          label={<Typography variant="body1">Schedule</Typography>}
        />
      </Grid>

      {schedule && (
        <Grid item md={2} xs={12}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDateTimePicker
              label={"Goes live at"}
              ampm={false}
              value={notification.scheduledAt}
              disabled={status.loading}
              renderInput={(props) => (
                <TextField
                  {...props}
                  size="large"
                  variant="outlined"
                  className={appStyle.inputs}
                  helperText={null}
                />
              )}
              onChange={(value) => {
                onValueChange(toTzTime(value.toDate()).format(), "scheduledAt");
                //setDate(value.toDate());
              }}
            />
          </LocalizationProvider>
        </Grid>
      )}
    </EditPage>
  );
};

export default NotificationEdit;
